"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { motion, useReducedMotion } from "framer-motion";
import { Phone, MessageSquare, Building2, CircleCheck as CheckCircle2, ArrowRight, Loader as Loader2, Sparkles } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
function InquiryFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center p-8 text-center bg-accent/30 rounded-none border border-border">
        <CheckCircle2 className="size-12 text-secondary mb-4" />
        <h3 className="text-xl font-bold font-serif text-foreground mb-2">
          Request received
        </h3>
        <p className="text-muted-foreground text-sm max-w-md mb-6">
          {successMessage || "Thank you. Sunil Singh will review your property data and get in touch shortly with customized RevPAR insights."}
        </p>
        <Button type="button" onClick={resetForm} variant="outline" className="border-border text-foreground hover:bg-muted">
          Submit another property
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField control={control} name="name" rules={{
        required: "Your name is required",
        minLength: {
          value: 2,
          message: "Minimum 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground text-xs uppercase tracking-wider font-semibold">
                Your name
              </FormLabel>
              <FormControl>
                <Input placeholder="Sunil Kumar" className="bg-background border-border text-foreground px-3 py-2 text-sm focus-visible:ring-secondary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="hotelName" rules={{
        required: "Hotel or property name is required",
        minLength: {
          value: 2,
          message: "Minimum 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground text-xs uppercase tracking-wider font-semibold">
                Hotel / Property name
              </FormLabel>
              <FormControl>
                <Input placeholder="e.g. Heritage Palms Resort" className="bg-background border-border text-foreground px-3 py-2 text-sm focus-visible:ring-secondary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <FormField control={control} name="city" rules={{
        required: "City is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground text-xs uppercase tracking-wider font-semibold">
                City / Location
              </FormLabel>
              <FormControl>
                <Input placeholder="Ujjain" className="bg-background border-border text-foreground px-3 py-2 text-sm focus-visible:ring-secondary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required",
        pattern: {
          value: /^[0-9+() -]{7,20}$/,
          message: "Please enter a valid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground text-xs uppercase tracking-wider font-semibold">
                Phone number
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+91 98765 43210" className="bg-background border-border text-foreground px-3 py-2 text-sm focus-visible:ring-secondary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="roomCount" rules={{
        required: "Room count is required",
        min: {
          value: 1,
          message: "Enter at least 1 room"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground text-xs uppercase tracking-wider font-semibold">
                Room count
              </FormLabel>
              <FormControl>
                <Input type="number" placeholder="e.g. 24" className="bg-background border-border text-foreground px-3 py-2 text-sm focus-visible:ring-secondary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-none text-destructive text-sm">
          {submitError}
        </div>}

      <div className="pt-2">
        <Button type="submit" disabled={isSubmitting} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-none h-11 text-sm font-semibold tracking-wide transition-colors">
          {isSubmitting ? <>
              <Loader2 className="animate-spin mr-2 size-4" />
              Calculating potential...
            </> : <>
              Request customized RevPAR analysis
              <ArrowRight className="ml-2 size-4 text-secondary" />
            </>}
        </Button>
      </div>
    </Form>;
}
export default function RevparInquiry() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  const whatsappUrl = "https://wa.me/917972195929?text=Hi%20Sunil%2C%20I%27m%20interested%20in%20Hotel%20Revenue%20Management%20Consulting%20services%20for%20my%20property%20in%20Ujjain.";
  return <section id="revparpotentialinquiry" data-nav="light" className="relative bg-background py-16 md:py-24 border-b border-border overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={containerVariants} className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Left Column: Context & Direct Contact */}
          <div className="lg:col-span-5 space-y-6">
            <div className="space-y-3">
              <Badge variant="outline" className="rounded-none border-secondary/40 text-secondary-foreground bg-accent/40 px-3 py-1 font-mono text-xs uppercase tracking-wider gap-1.5">
                <Sparkles className="size-3 text-secondary" />
                Revenue optimization inquiry
              </Badge>

              <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground tracking-tight leading-tight">
                Unlock your property’s true RevPAR potential.
              </h2>

              <p className="text-muted-foreground text-base leading-relaxed">
                Share key details about your hotel or resort in Ujjain. Sunil Singh will review your distribution matrix, pricing strategy, and room inventory to project your incremental revenue upside.
              </p>
            </div>

            <div className="p-5 border border-border bg-card/80 space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-accent/40 border border-secondary/30 text-secondary">
                  <Building2 className="size-5" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground">
                    Dedicated expert consultation
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    Hands-on audit by Sunil Singh for properties in Ujjain
                  </p>
                </div>
              </div>

              <Separator className="bg-border" />

              <div className="flex flex-col sm:flex-row gap-3 pt-1">
                <Button asChild variant="outline" className="flex-1 rounded-none border-border bg-background hover:bg-muted text-foreground justify-center gap-2 h-10 text-xs font-semibold">
                  <Link href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center">
                    <MessageSquare className="size-4 text-emerald-600" />
                    WhatsApp Sunil
                  </Link>
                </Button>

                <Button asChild variant="outline" className="flex-1 rounded-none border-border bg-background hover:bg-muted text-foreground justify-center gap-2 h-10 text-xs font-semibold">
                  <a href="tel:+917972195929" className="inline-flex items-center">
                    <Phone className="size-4 text-secondary" />
                    +91 79721 95929
                  </a>
                </Button>
              </div>
            </div>

            <div className="pt-1">
              <Link to="/LocationsPage" className="inline-flex items-center text-xs text-muted-foreground hover:text-foreground underline underline-offset-4 transition-colors">
                Explore other locations we serve
              </Link>
            </div>
          </div>

          {/* Right Column: Interactive Form */}
          <div className="lg:col-span-7">
            <Card className="rounded-none border border-border shadow-md bg-card">
              <CardHeader className="border-b border-border pb-5">
                <CardTitle className="text-xl font-serif font-bold text-foreground">
                  Property assessment inquiry
                </CardTitle>
                <CardDescription className="text-muted-foreground text-xs">
                  Fill in your property parameters to get a data-backed OTA revenue review.
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <FormProvider formId="revpar-potential-form" sectionName="RevparInquiry" defaultValues={{
                name: "",
                hotelName: "",
                city: "Ujjain",
                phone: "",
                roomCount: ""
              }} validationRules={{
                name: {
                  required: true,
                  minLength: 2
                },
                hotelName: {
                  required: true,
                  minLength: 2
                },
                city: {
                  required: true
                },
                phone: {
                  required: true
                },
                roomCount: {
                  required: true,
                  min: 1
                }
              }} submitText="Request customized RevPAR analysis" successMessage="Thank you! We will review your property data and reach out shortly.">
                  <InquiryFormContent />
                </FormProvider>
              </CardContent>
            </Card>
          </div>
        </motion.div>
      </div>
    </section>;
}